import { NavigationPage } from '@/app/components'
import { Divider, Icon, LoadingAnimation } from '@/components'
import { IconNames } from '@/components/Icon/Icon'
import useViewModel from './usePushNotificationSettingsViewModel'
import { AdaptiveCardRowLayout } from '@/app/layouts'
import { Grid, GridItem, Text, Toggle } from '@/components'
import clsx from 'clsx'
import PushNotificationsDisabledAlertMessage from './components/PushNotificationsDisabledAlertMessage'
import { LoadingContainer } from '@/components/Animations/FadeContainer/FadeContainer'

const PushNotificationSettingsGroup = ({
  title,
  icon,
  children,
}: {
  title: string
  icon: IconNames
  children: React.ReactNode
}) => {
  return (
    <div>
      <div className="flex items-center">
        <div
          className={clsx(
            'mr-2 p-1.5 flex justify-center items-center rounded-lg',
            'bg-purple-100 text-purple-500',
          )}
        >
          <Icon name={icon} size={20} />
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="subheader">{title}</Text>
        </div>
      </div>
      {children}
    </div>
  )
}

const PushNotificationSettingsRow = ({
  title,
  subtitle,
  bottomRule = true,
  checked,
  disabled,
  onChange,
}: {
  title: string
  subtitle: string
  bottomRule?: boolean
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) => {
  return (
    <AdaptiveCardRowLayout.CardRow
      bottomRule={bottomRule}
      marginClassName="mt-4 mb-8"
    >
      <Grid flow="column">
        <GridItem>
          <Text variant="body1">{title}</Text>
          <Text variant="body2" className="mt-2">
            {subtitle}
          </Text>
        </GridItem>
        <GridItem className="pt-1 pl-16 justify-self-end">
          <Toggle
            id={`push-notification-toggle-${title
              .toLowerCase()
              .replace(' ', '-')}`}
            checked={disabled ? false : checked}
            onChange={disabled ? undefined : onChange}
            disabled={disabled}
          />
        </GridItem>
      </Grid>
    </AdaptiveCardRowLayout.CardRow>
  )
}

export default function PushNotificationSettings() {
  const {
    isLoading,
    pushNotificationSettings,
    mobilePushEnabled,
    updateNotificationSetting,
  } = useViewModel()
  return (
    <NavigationPage
      id="push-notification-settings"
      title="Push notifications"
      subtitle="Customize your notifications from Optiwatt"
    >
      <LoadingContainer
        isLoading={isLoading}
        loadingContent={
          <div className="flex items-center justify-center w-full h-full">
            <LoadingAnimation type="falling" />
          </div>
        }
        loadedContent={
          <div className="flex flex-col gap-8">
            {!mobilePushEnabled && (
              <div className="border-b border-solid border-base-grey-100">
                <PushNotificationsDisabledAlertMessage />
              </div>
            )}
            {mobilePushEnabled && <Divider />}

            <PushNotificationSettingsGroup
              title="General notifications"
              icon="Bell"
            >
              <PushNotificationSettingsRow
                title="Demand response (DR) events"
                subtitle="Get notified about upcoming and current DR events"
                disabled={!mobilePushEnabled}
                checked={
                  pushNotificationSettings?.demand_response_event_notifications_enabled ??
                  false
                }
                onChange={(enabled) =>
                  updateNotificationSetting(
                    'demand_response_event_notifications_enabled',
                    enabled,
                  )
                }
              />
            </PushNotificationSettingsGroup>

            <PushNotificationSettingsGroup
              title="Thermostat notifications"
              icon="Thermometer"
            >
              <PushNotificationSettingsRow
                title="Save Money adjustments"
                subtitle="Get notified when Save Money makes an adjustment to your thermostat(s)"
                disabled={!mobilePushEnabled}
                bottomRule={false}
                checked={
                  pushNotificationSettings?.thermostat_cost_category_optimization_notifications_enabled ??
                  false
                }
                onChange={(enabled) =>
                  updateNotificationSetting(
                    'thermostat_cost_category_optimization_notifications_enabled',
                    enabled,
                  )
                }
              />
            </PushNotificationSettingsGroup>
          </div>
        }
      />
    </NavigationPage>
  )
}
